import { execute, getLinkSessionId } from '@/graphql/execute';
import { useOrganizations } from '@/hooks/useOrganizations';
import { quickUploadLayerMutation } from '@/queries/layer.query';
import { sharedLinkQuery } from '@/queries/project.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const QUICK_UPLOAD_ACCEPTED_EXTENSIONS = ['geojson', 'json', 'dxf', 'dwg', 'ifc', 'frag', 'xml'];
export const QUICK_UPLOAD_ACCEPT_ATTR = QUICK_UPLOAD_ACCEPTED_EXTENSIONS.map((e) => '.' + e).join(',');
export const QUICK_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(',');
      resolve(idx === -1 ? result : result.slice(idx + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const getExtension = (filename: string): string => {
  const m = filename.toLowerCase().match(/\.([^.]+)$/);
  return m ? m[1] : '';
};

/**
 * Resolves the session id used to scope ephemeral layers to a single browser session.
 * Logged-in users use a localStorage-stable user-session id; anonymous shared-link
 * viewers reuse the X-Link-Session id already managed by graphql/execute.
 */
const getQuickUploadSessionId = (): string => {
  const linkSessionId = getLinkSessionId();
  if (linkSessionId) return linkSessionId;

  const key = 'quick-upload-session-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

/**
 * Single source of truth for the quick-upload feature: format/quota/error
 * mapping, share-link gating, and the GraphQL mutation. Both the drag-and-drop
 * overlay and the explicit "+ Add temporary layer" buttons call `handleFiles`
 * from here so behaviour and toasts stay identical.
 */
export const useQuickUpload = (projectId: string) => {
  const { t } = useTranslation('project');
  const { hasRole } = useOrganizations();
  const queryClient = useQueryClient();

  const linkSessionId = getLinkSessionId();
  const isSharedLink = !!linkSessionId;

  const { data: sharedLinkData } = useQuery({
    queryKey: ['sharedLink'],
    queryFn: () => execute(sharedLinkQuery),
    enabled: isSharedLink,
    staleTime: 5 * 60_000,
  });

  const isAllowed = isSharedLink ? !!sharedLinkData?.sharedLink?.allowQuickUploads : hasRole('EDITOR' as any);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (file: File) => {
      const contentBase64 = await fileToBase64(file);
      return execute(quickUploadLayerMutation, {
        input: {
          projectId,
          filename: file.name,
          contentBase64,
          sessionId: getQuickUploadSessionId(),
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Layers', projectId] });
      queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
    },
  });

  const handleFiles = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files) return;
      const list = Array.from(files);
      for (const file of list) {
        const ext = getExtension(file.name);
        if (!QUICK_UPLOAD_ACCEPTED_EXTENSIONS.includes(ext)) {
          toast.error(t('quickUpload.unsupportedFormat', { filename: file.name }));
          continue;
        }
        if (file.size > QUICK_UPLOAD_MAX_BYTES) {
          toast.error(t('quickUpload.tooLarge', { filename: file.name }));
          continue;
        }
        try {
          await mutateAsync(file);
          toast.success(t('quickUpload.uploaded', { filename: file.name }));
        } catch (err: any) {
          const code = String(err?.message || '');
          if (code.includes('QUOTA_LAYER_LIMIT') || code.includes('QUOTA_BYTES_LIMIT')) {
            toast.error(t('quickUpload.quotaExceeded'));
          } else if (code.includes('STORAGE_QUOTA_EXCEEDED')) {
            toast.error(t('quickUpload.storageQuotaExceeded'));
          } else if (code.includes('UNSUPPORTED_FORMAT')) {
            toast.error(t('quickUpload.unsupportedFormat', { filename: file.name }));
          } else if (code.includes('TOO_LARGE')) {
            toast.error(t('quickUpload.tooLarge', { filename: file.name }));
          } else if (code.includes('INVALID_GEOJSON') || code.includes('INVALID_LANDXML')) {
            toast.error(t('quickUpload.invalidContent', { filename: file.name }));
          } else if (code.includes('QUICK_UPLOADS_NOT_ALLOWED')) {
            toast.error(t('quickUpload.notAllowed'));
          } else {
            toast.error(t('quickUpload.failed', { filename: file.name }));
          }
        }
      }
    },
    [mutateAsync, t],
  );

  return { handleFiles, isAllowed, isPending, isSharedLink };
};
